// ---------------------------------------------------------------------------
// Utility helpers shared by field & section components
// ---------------------------------------------------------------------------

/** Extract a class value by prefix (e.g. 'w-' from 'w-full' -> 'full') */
export function extractClassValue(className: string, prefix: string): string {
  const match = className.split(/\s+/).find((c) => c.startsWith(prefix))
  return match ? match.slice(prefix.length) : ''
}

/** Replace or add a class by prefix */
export function replaceOrAddClass(className: string, prefix: string, newClass: string): string {
  const classes = className.split(/\s+/).filter(Boolean).filter((c) => !c.startsWith(prefix))
  if (newClass) classes.push(newClass)
  return classes.join(' ')
}

/**
 * Convert a Tailwind color token (e.g. 'blue-500') to an approximate hex color
 * for swatch preview. Only covers common colors — falls back to gray for unknown.
 */
export function tailwindTokenToHex(token: string): string {
  if (!token) return '#e5e7eb'
  // Handle special values
  if (token === 'white') return '#ffffff'
  if (token === 'black') return '#000000'
  if (token === 'transparent') return 'transparent'
  if (token.startsWith('#')) return token

  const APPROX_COLORS: Record<string, Record<string, string>> = {
    slate: { '50': '#f8fafc', '100': '#f1f5f9', '200': '#e2e8f0', '300': '#cbd5e1', '400': '#94a3b8', '500': '#64748b', '600': '#475569', '700': '#334155', '800': '#1e293b', '900': '#0f172a', '950': '#020617' },
    gray: { '50': '#f9fafb', '100': '#f3f4f6', '200': '#e5e7eb', '300': '#d1d5db', '400': '#9ca3af', '500': '#6b7280', '600': '#4b5563', '700': '#374151', '800': '#1f2937', '900': '#111827', '950': '#030712' },
    red: { '50': '#fef2f2', '100': '#fee2e2', '200': '#fecaca', '300': '#fca5a5', '400': '#f87171', '500': '#ef4444', '600': '#dc2626', '700': '#b91c1c', '800': '#991b1b', '900': '#7f1d1d', '950': '#450a0a' },
    orange: { '50': '#fff7ed', '100': '#ffedd5', '200': '#fed7aa', '300': '#fdba74', '400': '#fb923c', '500': '#f97316', '600': '#ea580c', '700': '#c2410c', '800': '#9a3412', '900': '#7c2d12', '950': '#431407' },
    yellow: { '50': '#fefce8', '100': '#fef9c3', '200': '#fef08a', '300': '#fde047', '400': '#facc15', '500': '#eab308', '600': '#ca8a04', '700': '#a16207', '800': '#854d0e', '900': '#713f12', '950': '#422006' },
    green: { '50': '#f0fdf4', '100': '#dcfce7', '200': '#bbf7d0', '300': '#86efac', '400': '#4ade80', '500': 'hsl(var(--success))', '600': '#16a34a', '700': '#15803d', '800': '#166534', '900': '#14532d', '950': '#052e16' },
    blue: { '50': '#eff6ff', '100': '#dbeafe', '200': '#bfdbfe', '300': '#93c5fd', '400': '#60a5fa', '500': '#3b82f6', '600': '#2563eb', '700': '#1d4ed8', '800': '#1e40af', '900': '#1e3a8a', '950': '#172554' },
    indigo: { '50': '#eef2ff', '100': '#e0e7ff', '200': '#c7d2fe', '300': '#a5b4fc', '400': '#818cf8', '500': '#6366f1', '600': '#4f46e5', '700': '#4338ca', '800': '#3730a3', '900': '#312e81', '950': '#1e1b4b' },
    purple: { '50': '#faf5ff', '100': '#f3e8ff', '200': '#e9d5ff', '300': '#d8b4fe', '400': '#c084fc', '500': '#a855f7', '600': '#9333ea', '700': '#7e22ce', '800': '#6b21a8', '900': '#581c87', '950': '#3b0764' },
    pink: { '50': '#fdf2f8', '100': '#fce7f3', '200': '#fbcfe8', '300': '#f9a8d4', '400': '#f472b6', '500': '#ec4899', '600': '#db2777', '700': '#be185d', '800': '#9d174d', '900': '#831843', '950': '#500724' },
  }

  const parts = token.split('-')
  if (parts.length === 2) {
    const [name, shade] = parts
    if (APPROX_COLORS[name] && APPROX_COLORS[name][shade]) {
      return APPROX_COLORS[name][shade]
    }
  }

  return '#e5e7eb' // fallback gray-200
}

// ---------------------------------------------------------------------------
// Option constants for select fields
// ---------------------------------------------------------------------------

export const VARIANT_OPTIONS = [
  { label: 'Default', value: 'default' },
  { label: 'Outline', value: 'outline' },
  { label: 'Ghost', value: 'ghost' },
  { label: 'Destructive', value: 'destructive' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Link', value: 'link' },
]

export const LAYOUT_TYPE_OPTIONS = [
  { label: 'Flex', value: 'flex' },
  { label: 'Grid', value: 'grid' },
  { label: 'Absolute', value: 'absolute' },
]

export const DIRECTION_OPTIONS = [
  { label: 'Row', value: 'row' },
  { label: 'Column', value: 'column' },
]

export const ALIGN_OPTIONS = [
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
  { label: 'Stretch', value: 'stretch' },
  { label: 'Baseline', value: 'baseline' },
]

export const JUSTIFY_OPTIONS = [
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
  { label: 'Between', value: 'between' },
  { label: 'Around', value: 'around' },
  { label: 'Evenly', value: 'evenly' },
]

export const ROUNDED_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Small', value: 'sm' },
  { label: 'Default', value: 'DEFAULT' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' },
  { label: 'XL', value: 'xl' },
  { label: '2XL', value: '2xl' },
  { label: '3XL', value: '3xl' },
  { label: 'Full', value: 'full' },
]

export const FONT_SIZE_OPTIONS = [
  { label: 'XS', value: 'xs' },
  { label: 'SM', value: 'sm' },
  { label: 'Base', value: 'base' },
  { label: 'LG', value: 'lg' },
  { label: 'XL', value: 'xl' },
  { label: '2XL', value: '2xl' },
  { label: '3XL', value: '3xl' },
  { label: '4XL', value: '4xl' },
  { label: '5XL', value: '5xl' },
]

export const FONT_WEIGHT_OPTIONS = [
  { label: 'Thin', value: 'thin' },
  { label: 'Light', value: 'light' },
  { label: 'Normal', value: 'normal' },
  { label: 'Medium', value: 'medium' },
  { label: 'Semibold', value: 'semibold' },
  { label: 'Bold', value: 'bold' },
  { label: 'Extrabold', value: 'extrabold' },
  { label: 'Black', value: 'black' },
]

export const BORDER_WIDTH_OPTIONS = [
  { label: 'None', value: '0' },
  { label: '1px', value: '' },
  { label: '2px', value: '2' },
  { label: '4px', value: '4' },
  { label: '8px', value: '8' },
]

export const BORDER_STYLE_OPTIONS = [
  { label: 'Solid', value: 'solid' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
  { label: 'None', value: 'none' },
]

export const SHADOW_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'SM', value: 'sm' },
  { label: 'Default', value: '' },
  { label: 'MD', value: 'md' },
  { label: 'LG', value: 'lg' },
  { label: 'XL', value: 'xl' },
  { label: '2XL', value: '2xl' },
]

export const LINE_HEIGHT_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Tight', value: 'tight' },
  { label: 'Snug', value: 'snug' },
  { label: 'Normal', value: 'normal' },
  { label: 'Relaxed', value: 'relaxed' },
  { label: 'Loose', value: 'loose' },
]

export const OVERFLOW_OPTIONS = [
  { label: 'Visible', value: 'visible' },
  { label: 'Hidden', value: 'hidden' },
  { label: 'Auto', value: 'auto' },
  { label: 'Scroll', value: 'scroll' },
]

export const POSITION_OPTIONS = [
  { label: 'Static', value: 'static' },
  { label: 'Relative', value: 'relative' },
  { label: 'Sticky', value: 'sticky' },
]

// ---------------------------------------------------------------------------
// Per-type variant / size options (CVA-aligned)
// ---------------------------------------------------------------------------

type SelectOption = { label: string; value: string }

/** Variant options per component type, sourced from CVA definitions */
export const VARIANT_OPTIONS_BY_TYPE: Record<string, SelectOption[]> = {
  button: [
    { label: 'Default', value: 'default' },
    { label: 'Secondary', value: 'secondary' },
    { label: 'Outline', value: 'outline' },
    { label: 'Destructive', value: 'destructive' },
    { label: 'Ghost', value: 'ghost' },
    { label: 'Link', value: 'link' },
  ],
  badge: [
    { label: 'Default', value: 'default' },
    { label: 'Secondary', value: 'secondary' },
    { label: 'Destructive', value: 'destructive' },
    { label: 'Outline', value: 'outline' },
  ],
  alert: [
    { label: 'Default', value: 'default' },
    { label: 'Destructive', value: 'destructive' },
  ],
  toggle: [
    { label: 'Default', value: 'default' },
    { label: 'Outline', value: 'outline' },
  ],
  input: [
    { label: 'Default', value: 'default' },
    { label: 'Outline', value: 'outline' },
  ],
  select: [
    { label: 'Default', value: 'default' },
    { label: 'Outline', value: 'outline' },
  ],
  textarea: [
    { label: 'Default', value: 'default' },
    { label: 'Outline', value: 'outline' },
  ],
}

/** Size options per component type, sourced from CVA definitions */
export const SIZE_OPTIONS_BY_TYPE: Record<string, SelectOption[]> = {
  button: [
    { label: 'Default', value: 'default' },
    { label: 'Small', value: 'sm' },
    { label: 'Large', value: 'lg' },
    { label: 'Icon', value: 'icon' },
  ],
  toggle: [
    { label: 'Default', value: 'default' },
    { label: 'Small', value: 'sm' },
    { label: 'Large', value: 'lg' },
  ],
}

/** Types that use a variant prop */
export const VARIANT_TYPES = new Set([
  'button', 'badge', 'input', 'select', 'textarea', 'alert', 'toggle',
])

/** Types that use a size prop */
export const SIZE_TYPES = new Set(['button', 'toggle'])

// ---------------------------------------------------------------------------
// Variant-class-token detection for className warning
// ---------------------------------------------------------------------------

/** Exact Tailwind tokens that are variant-related (from Normalizer patterns).
 *  Uses exact Set.has() matching — NOT prefix matching. */
export const VARIANT_CLASS_TOKENS = new Set([
  // Button/badge variant tokens
  'bg-primary', 'text-primary-foreground', 'hover:bg-primary/90',
  'bg-secondary', 'text-secondary-foreground', 'hover:bg-secondary/80',
  'bg-destructive', 'text-destructive-foreground', 'hover:bg-destructive/90',
  'border-input', 'bg-background', 'hover:bg-accent', 'hover:text-accent-foreground',
  'underline-offset-4', 'hover:underline',
  // Size tokens that size prop controls
  'h-8', 'h-9', 'h-10', 'h-11',
  'px-3', 'px-4', 'px-8',
])

/**
 * Detect variant-related Tailwind classes in a className string.
 * Uses exact token matching (Set.has), NOT prefix matching.
 * Returns array of detected variant tokens.
 */
export function detectVariantClasses(className: string): string[] {
  if (!className) return []
  return className
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => VARIANT_CLASS_TOKENS.has(token))
}
